/**
 * forms.js — accessible form controls.
 *
 * DEFECTS IN v1 THAT THIS FIXES
 *  - `htmlFor` and `aria-*` appeared a combined TWO times in the whole source.
 *    The ~40 controls inside each invoice grid had no label, no aria-label and
 *    no accessible name at all.
 *  - Validation surfaced one error at a time through a transient toast, with
 *    no inline message, no aria-invalid, no aria-describedby and no focus
 *    management.
 *  - Controlled inputs were rendered with `onChange={null}`, and
 *    `value={x && x}` produced undefined, triggering React's
 *    controlled/uncontrolled warning.
 *  - Money and quantity fields were `type="text"` with no numeric validation,
 *    so "abc" was accepted and stored.
 */

import {
  Children,
  Fragment,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { AddIcon, CheckIcon, ChevronIcon, CloseIcon, SearchIcon } from './icons';
import { cx } from './primitives';
import { toPaise, toRupees } from '../../lib/money';

/* ------------------------------------------------------------------ *
 * Field wrapper — owns the label/hint/error wiring
 * ------------------------------------------------------------------ */

export function Field({
  label,
  hint,
  error,
  required = false,
  htmlFor,
  className,
  children,
  span2 = false,
  /** Optional id put on the <label>, so a non-native control (the custom
   *  Select) can point `aria-labelledby` at it. Ignored when absent. */
  labelId,
}) {
  return (
    <div className={cx('field', span2 && 'field-span-2', className)}>
      {label && (
        <label className="field-label" id={labelId} htmlFor={htmlFor}>
          {label}
          {required && (
            <span className="field-required" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}
      {children}
      {error ? (
        <span className="field-error" role="alert">
          {error}
        </span>
      ) : (
        hint && <span className="field-hint">{hint}</span>
      )}
    </div>
  );
}

/**
 * Build the aria wiring for a control inside a Field.
 * Returns the props to spread plus the ids the Field needs.
 */
function useFieldAria({ id, error, hint }) {
  const generated = useId();
  const controlId = id || generated;
  const errorId = `${controlId}-error`;
  const hintId = `${controlId}-hint`;
  const describedBy = [error ? errorId : null, hint && !error ? hintId : null]
    .filter(Boolean)
    .join(' ');
  return {
    controlId,
    errorId,
    hintId,
    aria: {
      id: controlId,
      'aria-invalid': error ? true : undefined,
      'aria-describedby': describedBy || undefined,
    },
  };
}

/* ------------------------------------------------------------------ *
 * Text input
 * ------------------------------------------------------------------ */

/**
 * `className` GOES TO THE FIELD, NOT TO THE CONTROL.
 *
 * It used to go to the control, and that is a trap with no symptom: writing
 * `<Textarea className="mt-3" />` to space a block away from the one above it
 * put the margin between the LABEL and its box, and left the block itself
 * flush — which looks almost right, so it survives review. It produced a live
 * defect in the stock-transfer dialog.
 *
 * The field wrapper is what a caller means nine times in ten, because
 * `className` on a form control is nearly always layout. `inputClassName` is
 * there for the tenth.
 */
export const Input = forwardRef(function Input(
  { label, hint, error, required, className, inputClassName, id, span2, numeric = false, ...rest },
  ref
) {
  const { controlId, aria } = useFieldAria({ id, error, hint });
  return (
    <Field
      label={label}
      hint={hint}
      error={error}
      required={required}
      htmlFor={controlId}
      span2={span2}
      className={className}
    >
      <input
        ref={ref}
        className={cx('input', numeric && 'input-numeric', inputClassName)}
        required={required}
        {...aria}
        {...rest}
      />
    </Field>
  );
});

/** A bare input for use inside the invoice line grid, where the column header
 *  is the label. It still gets an accessible name via aria-label. */
export const GridInput = forwardRef(function GridInput(
  { ariaLabel, numeric = false, error, className, ...rest },
  ref
) {
  return (
    <input
      ref={ref}
      className={cx('input', numeric && 'input-numeric', className)}
      aria-label={ariaLabel}
      aria-invalid={error ? true : undefined}
      {...rest}
    />
  );
});

/** `className` goes to the field. See the note on `Input`. */
export const Textarea = forwardRef(function Textarea(
  { label, hint, error, required, className, inputClassName, id, span2, ...rest },
  ref
) {
  const { controlId, aria } = useFieldAria({ id, error, hint });
  return (
    <Field
      label={label}
      hint={hint}
      error={error}
      required={required}
      htmlFor={controlId}
      span2={span2}
      className={className}
    >
      <textarea ref={ref} className={cx('textarea', inputClassName)} required={required} {...aria} {...rest} />
    </Field>
  );
});

/* ------------------------------------------------------------------ *
 * Select — a custom listbox that replaces the native <select>
 * ------------------------------------------------------------------ */

/** How tall the popup may get before it scrolls. Also clamped by the space
 *  actually available on screen, which is why it lives here and not in CSS. */
const SELECT_LIST_MAX_HEIGHT = 288;
/** Gap between the trigger and the popup, and the popup and the viewport. */
const SELECT_LIST_OFFSET = 4;
const SELECT_VIEWPORT_PAD = 8;
/* Below this much room to the right of the trigger, the popup flips to
   right-alignment rather than being squeezed. */
const SELECT_LIST_MIN_ROOM = 240;
/** Idle time after which the type-ahead buffer resets, matching the platform. */
const TYPEAHEAD_RESET_MS = 700;

/** Flatten a React node down to its text, so `<option>Foo {bar}</option>` still
 *  yields a searchable, type-ahead-able string. */
function nodeText(node) {
  if (node === null || node === undefined || node === false || node === true) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join('');
  if (isValidElement(node)) return nodeText(node.props?.children);
  return '';
}

/**
 * Read `<option>` / `<optgroup>` children into the same option shape the
 * `options` prop uses. Only one caller writes its options as children
 * (ItemForm's GST rate list, which needs `<optgroup>`), but the form is part
 * of the native `<select>` contract so it is supported unconditionally.
 */
function optionsFromChildren(children) {
  const out = [];
  const walk = (nodes, group) => {
    Children.forEach(nodes, (child) => {
      if (!isValidElement(child)) return;
      if (child.type === Fragment) {
        walk(child.props.children, group);
      } else if (child.type === 'optgroup') {
        walk(child.props.children, child.props.label);
      } else if (child.type === 'option') {
        const { value, disabled, children: kids, label: optLabel } = child.props;
        out.push({
          value: value === undefined ? nodeText(kids) : value,
          label: optLabel || nodeText(kids),
          disabled,
          group,
        });
      }
    });
  };
  walk(children);
  return out;
}

/** Native selects stringify their value; comparisons must too, or a numeric
 *  `value` would never match a string option value. */
const sameValue = (a, b) => String(a ?? '') === String(b ?? '');

/**
 * A fully custom, portalled listbox with the public API of the old native
 * `<select>` wrapper: `label`, `options`, `value`, `onChange`, `placeholder`,
 * `hint`, `error`, `required`, `disabled`, `id`, `span2`, `aria-label`, a
 * forwarded ref, and `<option>`/`<optgroup>` children.
 *
 * COMPATIBILITY SHIM — deliberate, do not "clean up".
 * Roughly forty call sites across src/features read the new value as
 * `onChange={(e) => setX(e.target.value)}`, because this component used to
 * render a real `<select>`. Rather than touch all of them, `onChange` is
 * handed a synthetic event-like object carrying `target` and `currentTarget`
 * (the same object, as the DOM does for a change event) with `value`, `name`
 * and `id` on it. `value` is always a string, exactly as a native select
 * reports it, so callers that do `Number(e.target.value)` keep working. The
 * chosen option is passed as a second argument for anyone who wants the whole
 * record instead.
 *
 * The forwarded ref points at the trigger button, so the existing
 * focus-the-first-invalid-field logic in the party and item forms still works.
 */
export const Select = forwardRef(function Select(
  {
    label,
    hint,
    error,
    required,
    options,
    placeholder,
    className,
    id,
    span2,
    children,
    value,
    onChange,
    disabled,
    name,
    emptyMessage = 'No options',
    ...rest
  },
  ref
) {
  const { controlId, aria } = useFieldAria({ id, error, hint });
  const labelId = `${controlId}-label`;
  const valueId = `${controlId}-value`;
  const listId = `${controlId}-listbox`;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [position, setPosition] = useState(null);

  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const typeahead = useRef({ buffer: '', at: 0 });

  // `options` wins; children are the fallback form. Memoised on the children
  // reference so a parent re-render does not rebuild the list every time.
  const items = useMemo(() => {
    if (options && options.length) return options;
    if (children) return optionsFromChildren(children);
    return options || [];
  }, [options, children]);

  const selectedIndex = useMemo(
    () => items.findIndex((o) => sameValue(o.value, value)),
    [items, value]
  );
  const selected = selectedIndex >= 0 ? items[selectedIndex] : null;

  const setTriggerRef = useCallback(
    (node) => {
      triggerRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref]
  );

  /* --- positioning -------------------------------------------------- */

  // Fixed positioning against the trigger's viewport rect. This is what lets
  // the popup escape a scrolling table, a card with `overflow: hidden` or a
  // modal — none of which can clip an element parented to <body>.
  const reposition = useCallback(() => {
    const node = triggerRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom - SELECT_LIST_OFFSET - SELECT_VIEWPORT_PAD;
    const above = rect.top - SELECT_LIST_OFFSET - SELECT_VIEWPORT_PAD;
    // Flip up only when below genuinely cannot hold the list and above is roomier.
    const flip = below < Math.min(SELECT_LIST_MAX_HEIGHT, above) && above > below;
    const space = Math.max(flip ? above : below, 96);
    /* The popup is as wide as its widest option, never narrower than the
       trigger. Pinning it to the trigger's width is what was clipping
       "Yesterday" and "Last 30 days" down to "Yester…" — a date filter is
       unusable if you cannot read the range you are picking. Growing it
       rightwards can run off-screen, so when the trigger sits near the
       right edge the popup is anchored by its right edge instead and grows
       leftwards, and either way it is capped at the room actually there. */
    const roomRight = window.innerWidth - rect.left - SELECT_VIEWPORT_PAD;
    const roomLeft = rect.right - SELECT_VIEWPORT_PAD;
    const alignRight = roomRight < SELECT_LIST_MIN_ROOM && roomLeft > roomRight;
    const next = {
      left: alignRight ? undefined : rect.left,
      right: alignRight ? window.innerWidth - rect.right : undefined,
      minWidth: rect.width,
      maxWidth: Math.max(rect.width, Math.floor(alignRight ? roomLeft : roomRight)),
      maxHeight: Math.min(SELECT_LIST_MAX_HEIGHT, space),
      flip,
      top: flip ? undefined : rect.bottom + SELECT_LIST_OFFSET,
      bottom: flip ? window.innerHeight - rect.top + SELECT_LIST_OFFSET : undefined,
    };
    // Scroll fires continuously; re-render only when the numbers actually move.
    setPosition((prev) =>
      prev && Object.keys(next).every((k) => prev[k] === next[k]) ? prev : next
    );
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    reposition();
    // `true` catches scrolls in any ancestor scroller, not just the window.
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  /* --- open/close --------------------------------------------------- */

  const close = useCallback((refocus = true) => {
    setOpen(false);
    setActiveIndex(-1);
    if (refocus) triggerRef.current?.focus();
  }, []);

  const openList = useCallback(
    (startIndex) => {
      if (disabled) return;
      setActiveIndex(
        startIndex !== undefined
          ? startIndex
          : selectedIndex >= 0
            ? selectedIndex
            : placeholder
              ? -1
              : items.findIndex((o) => !o.disabled)
      );
      setOpen(true);
    },
    [disabled, items, selectedIndex, placeholder]
  );

  // Click outside. The popup is portalled, so "inside" means either the
  // trigger or the list — a DOM containment test on the wrapper alone
  // would treat every click on an option as an outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (triggerRef.current?.contains(event.target)) return;
      if (listRef.current?.contains(event.target)) return;
      close(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open, close]);

  // Escape is intercepted at the WINDOW capture phase on purpose. Modal's
  // focus trap listens for Escape on `document` in the capture phase, and a
  // document listener registered earlier would otherwise close the whole
  // dialog out from under an open dropdown. Window capture runs first.
  useEffect(() => {
    if (!open) return undefined;
    const onEscape = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      close();
    };
    window.addEventListener('keydown', onEscape, true);
    return () => window.removeEventListener('keydown', onEscape, true);
  }, [open, close]);

  // Keep the active option in view while arrowing through a long list.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const node = listRef.current?.querySelector(`[data-index="${activeIndex}"]`);
    // Optional-called: jsdom and older WebViews do not implement it.
    node?.scrollIntoView?.({ block: 'nearest' });
  }, [open, activeIndex, position]);

  /* --- selection ---------------------------------------------------- */

  const commit = useCallback(
    (index) => {
      const option = items[index];
      if (!option || option.disabled) return;
      const nextValue = String(option.value ?? '');
      if (!sameValue(nextValue, value)) {
        const target = { value: nextValue, name: name ?? '', id: controlId };
        onChange?.({ target, currentTarget: target, type: 'change' }, option);
      }
      close();
    },
    [items, value, name, controlId, onChange, close]
  );

  const clearToPlaceholder = useCallback(() => {
    if (sameValue('', value)) {
      close();
      return;
    }
    const target = { value: '', name: name ?? '', id: controlId };
    onChange?.({ target, currentTarget: target, type: 'change' }, null);
    close();
  }, [value, name, controlId, onChange, close]);

  /* --- keyboard ----------------------------------------------------- */

  // Index -1 is the placeholder row, when there is one. Both helpers skip
  // disabled options, and neither wraps around — a native select stops at the
  // ends rather than cycling.
  const step = useCallback(
    (from, delta) => {
      const last = items.length - 1;
      const min = placeholder ? -1 : 0;
      let i = from;
      for (let guard = 0; guard <= items.length + 1; guard += 1) {
        i += delta;
        if (i < min || i > last) return from;
        if (i === -1 || !items[i].disabled) return i;
      }
      return from;
    },
    [items, placeholder]
  );

  const edge = useCallback(
    (fromStart) => {
      if (fromStart && placeholder) return -1;
      const order = fromStart
        ? items.map((_, i) => i)
        : items.map((_, i) => items.length - 1 - i);
      const found = order.find((i) => !items[i].disabled);
      return found === undefined ? (placeholder ? -1 : -1) : found;
    },
    [items, placeholder]
  );

  /** Type-ahead, the behaviour people rely on without noticing it exists:
   *  typing "ke" jumps to Kerala. Repeating one letter cycles the options
   *  beginning with it, exactly as a native select does. */
  const runTypeahead = useCallback(
    (char, from) => {
      const now = Date.now();
      const state = typeahead.current;
      state.buffer = now - state.at > TYPEAHEAD_RESET_MS ? char : state.buffer + char;
      state.at = now;

      const query = state.buffer.toLowerCase();
      const cycling = query.length > 1 && [...query].every((c) => c === query[0]);
      const needle = cycling ? query[0] : query;
      const start = query.length === 1 || cycling ? from + 1 : from < 0 ? 0 : from;

      for (let n = 0; n < items.length; n += 1) {
        const i = (start + n + items.length) % items.length;
        const option = items[i];
        if (option.disabled) continue;
        if (String(option.label ?? '').toLowerCase().startsWith(needle)) return i;
      }
      return -1;
    },
    [items]
  );

  const handleKeyDown = (event) => {
    if (disabled) return;
    const { key, altKey } = event;

    if (!open) {
      if (key === 'Enter' || key === ' ' || key === 'Spacebar' || key === 'ArrowDown' || key === 'ArrowUp') {
        event.preventDefault();
        openList();
        return;
      }
      if (key === 'Home' || key === 'End') {
        event.preventDefault();
        openList(edge(key === 'Home'));
        return;
      }
      // Closed-state type-ahead changes the value outright, as native does.
      if (key.length === 1 && !event.ctrlKey && !event.metaKey && !altKey) {
        const match = runTypeahead(key, selectedIndex);
        if (match >= 0) {
          event.preventDefault();
          commit(match);
        }
      }
      return;
    }

    switch (key) {
      case 'ArrowDown':
        event.preventDefault();
        if (altKey) close();
        else setActiveIndex((i) => step(i, 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (altKey) close();
        else setActiveIndex((i) => step(i, -1));
        break;
      case 'PageDown':
        event.preventDefault();
        setActiveIndex((i) => {
          let next = i;
          for (let n = 0; n < 10; n += 1) next = step(next, 1);
          return next;
        });
        break;
      case 'PageUp':
        event.preventDefault();
        setActiveIndex((i) => {
          let next = i;
          for (let n = 0; n < 10; n += 1) next = step(next, -1);
          return next;
        });
        break;
      case 'Home':
        event.preventDefault();
        setActiveIndex(edge(true));
        break;
      case 'End':
        event.preventDefault();
        setActiveIndex(edge(false));
        break;
      case 'Enter':
      case ' ':
      case 'Spacebar':
        event.preventDefault();
        if (activeIndex >= 0) commit(activeIndex);
        else if (placeholder) clearToPlaceholder();
        break;
      case 'Tab':
        // Native selects keep whatever is highlighted when focus leaves, so
        // the highlighted option is committed rather than discarded. Escape
        // is the "abandon" key; Tab is not. Not prevented — focus must move.
        if (activeIndex >= 0 && !items[activeIndex]?.disabled) commit(activeIndex);
        else if (activeIndex === -1 && placeholder) clearToPlaceholder();
        close(false);
        break;
      default:
        if (key.length === 1 && !event.ctrlKey && !event.metaKey && !altKey) {
          const match = runTypeahead(key, activeIndex);
          if (match >= 0) {
            event.preventDefault();
            setActiveIndex(match);
          }
        }
    }
  };

  /* --- render ------------------------------------------------------- */

  const ariaLabel = rest['aria-label'];
  const hasVisibleLabel = Boolean(label);
  // The accessible name of a select-only combobox is "<label>, <value>", built
  // from ids — putting aria-label on the button alone would silence the value.
  const labelledBy = hasVisibleLabel
    ? `${labelId} ${valueId}`
    : ariaLabel
      ? `${controlId}-sr-label ${valueId}`
      : undefined;
  const triggerRest = { ...rest };
  if (labelledBy) delete triggerRest['aria-label'];

  const activeOptionId = open && activeIndex >= 0 ? `${controlId}-opt-${activeIndex}` : undefined;

  /* THE FIELD IS AS WIDE AS ITS WIDEST OPTION, ALWAYS.
     Sized by whatever happens to be selected, the control shrinks to "Today"
     and stretches to "This financial year" as you use it, and the popup is
     forever wider than the thing that opened it. An invisible copy of the
     longest label, stacked behind the value, settles the width once and for
     all. Only plain-string labels can be measured this way; a React node is
     skipped rather than guessed at. */
  const widestLabel = useMemo(() => {
    let widest = typeof placeholder === 'string' ? placeholder : '';
    for (const option of items) {
      if (typeof option.label === 'string' && option.label.length > widest.length) {
        widest = option.label;
      }
    }
    return widest;
  }, [items, placeholder]);

  const list = (
    <ul
      ref={listRef}
      id={listId}
      role="listbox"
      className={cx('select-list', position?.flip && 'select-list-flipped')}
      aria-label={hasVisibleLabel ? label : ariaLabel}
      style={
        position
          ? {
              left: position.left,
              right: position.right,
              minWidth: position.minWidth,
              maxWidth: position.maxWidth,
              maxHeight: position.maxHeight,
              ...(position.flip ? { bottom: position.bottom } : { top: position.top }),
            }
          : { opacity: 0 }
      }
      // Keep DOM focus on the trigger: this is an aria-activedescendant
      // listbox, so the popup must never steal it or Escape would have
      // nothing to return focus to.
      onMouseDown={(event) => event.preventDefault()}
    >
      {items.length === 0 && (
        <li className="select-empty" role="presentation">
          {emptyMessage}
        </li>
      )}
      {placeholder && (
        <li
          id={`${controlId}-opt--1`}
          role="option"
          aria-selected={selectedIndex < 0}
          className="select-option select-option-placeholder"
          data-index="-1"
          data-active={activeIndex === -1 ? 'true' : undefined}
          onMouseEnter={() => setActiveIndex(-1)}
          onClick={clearToPlaceholder}
        >
          <span className="select-option-body">
            <span className="select-option-title">{placeholder}</span>
          </span>
          {selectedIndex < 0 && <CheckIcon size={14} aria-hidden="true" />}
        </li>
      )}
      {items.map((option, index) => {
        const isSelected = index === selectedIndex;
        const Icon = option.icon;
        const showGroup = option.group && option.group !== items[index - 1]?.group;
        return (
          <Fragment key={`${option.value}-${index}`}>
            {showGroup && (
              <li className="select-group" role="presentation">
                {option.group}
              </li>
            )}
            <li
              id={`${controlId}-opt-${index}`}
              role="option"
              aria-selected={isSelected}
              aria-disabled={option.disabled || undefined}
              className="select-option"
              data-index={index}
              data-active={index === activeIndex ? 'true' : undefined}
              data-disabled={option.disabled ? 'true' : undefined}
              onMouseEnter={() => !option.disabled && setActiveIndex(index)}
              onClick={() => commit(index)}
            >
              {Icon && <Icon size={14} aria-hidden="true" />}
              <span className="select-option-body">
                <span className="select-option-title">{option.label}</span>
                {option.meta && <span className="select-option-meta">{option.meta}</span>}
              </span>
              {isSelected && <CheckIcon size={14} aria-hidden="true" />}
            </li>
          </Fragment>
        );
      })}
    </ul>
  );

  return (
    <Field
      label={label}
      hint={hint}
      error={error}
      required={required}
      htmlFor={controlId}
      labelId={labelId}
      span2={span2}
    >
      <div className="select-wrap" data-open={open ? 'true' : 'false'}>
        {!hasVisibleLabel && ariaLabel && (
          <span id={`${controlId}-sr-label`} className="sr-only">
            {ariaLabel}
          </span>
        )}
        <button
          {...triggerRest}
          {...aria}
          ref={setTriggerRef}
          type="button"
          className={cx('select', 'select-trigger', className)}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-labelledby={labelledBy}
          aria-activedescendant={activeOptionId}
          aria-required={required || undefined}
          disabled={disabled}
          onClick={() => (open ? close() : openList())}
          onKeyDown={handleKeyDown}
        >
          <span className="select-value-box">
            <span
              id={valueId}
              className={cx('select-value', !selected && 'select-value-placeholder')}
              title={typeof selected?.label === 'string' ? selected.label : undefined}
            >
              {selected ? selected.label : placeholder || ' '}
            </span>
            {widestLabel ? (
              <span className="select-sizer" aria-hidden="true">
                {widestLabel}
              </span>
            ) : null}
          </span>
          <ChevronIcon className="select-caret" size={14} aria-hidden="true" />
        </button>
        {open && typeof document !== 'undefined' && createPortal(list, document.body)}
      </div>
    </Field>
  );
});

/* ------------------------------------------------------------------ *
 * Money input
 * ------------------------------------------------------------------ */

/**
 * A money field whose VALUE IS INTEGER PAISE.
 *
 * The component keeps a local string while the user types (so "12." and "0.5"
 * behave) and emits paise on every change. This is the boundary where rupees
 * become paise; nothing downstream ever sees a float.
 */
export const MoneyInput = forwardRef(function MoneyInput(
  { label, hint, error, required, value, onChange, id, span2, disabled, placeholder = '0.00', ...rest },
  ref
) {
  const { controlId, aria } = useFieldAria({ id, error, hint });
  const [text, setText] = useState(() => (value ? String(toRupees(value)) : ''));
  const lastEmitted = useRef(value);

  // Keep in sync when the value changes from outside (e.g. a recalculation),
  // but never fight the user while they are mid-keystroke.
  useEffect(() => {
    if (value !== lastEmitted.current) {
      setText(value ? String(toRupees(value)) : '');
      lastEmitted.current = value;
    }
  }, [value]);

  const handleChange = (event) => {
    const next = event.target.value;
    // Allow digits, one dot, up to two decimals, and a leading minus.
    if (next !== '' && !/^-?\d*\.?\d{0,2}$/.test(next)) return;
    setText(next);
    const paise = toPaise(next);
    lastEmitted.current = paise;
    onChange?.(paise);
  };

  const handleBlur = () => {
    if (text === '' || text === '-') {
      setText('');
      return;
    }
    setText(String(toRupees(toPaise(text))));
  };

  return (
    <Field label={label} hint={hint} error={error} required={required} htmlFor={controlId} span2={span2}>
      <div className="input-group">
        <span className="input-affix" aria-hidden="true">₹</span>
        <input
          ref={ref}
          className="input input-numeric"
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={text}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={placeholder}
          {...aria}
          {...rest}
        />
      </div>
    </Field>
  );
});

/** The same control, unlabelled, for the invoice line grid. */
export function GridMoneyInput({ value, onChange, ariaLabel, disabled }) {
  const [text, setText] = useState(() => (value ? String(toRupees(value)) : ''));
  const lastEmitted = useRef(value);

  useEffect(() => {
    if (value !== lastEmitted.current) {
      setText(value ? String(toRupees(value)) : '');
      lastEmitted.current = value;
    }
  }, [value]);

  return (
    <input
      className="input input-numeric"
      type="text"
      inputMode="decimal"
      autoComplete="off"
      aria-label={ariaLabel}
      disabled={disabled}
      value={text}
      onChange={(e) => {
        const next = e.target.value;
        if (next !== '' && !/^-?\d*\.?\d{0,2}$/.test(next)) return;
        setText(next);
        const paise = toPaise(next);
        lastEmitted.current = paise;
        onChange?.(paise);
      }}
      onBlur={() => setText(text === '' ? '' : String(toRupees(toPaise(text))))}
    />
  );
}

/** Quantity input: numeric, bounded, never accepts letters. */
export function QuantityInput({ value, onChange, ariaLabel, min = 0, max = 1e9, disabled }) {
  const [text, setText] = useState(() => (value === 0 || value ? String(value) : ''));
  const last = useRef(value);

  useEffect(() => {
    if (value !== last.current) {
      setText(value === 0 || value ? String(value) : '');
      last.current = value;
    }
  }, [value]);

  return (
    <input
      className="input input-numeric"
      type="text"
      inputMode="decimal"
      autoComplete="off"
      aria-label={ariaLabel}
      disabled={disabled}
      value={text}
      onChange={(e) => {
        const next = e.target.value;
        if (next !== '' && !/^\d*\.?\d{0,3}$/.test(next)) return;
        setText(next);
        const n = next === '' ? 0 : Number(next);
        const clamped = Number.isFinite(n) ? Math.min(Math.max(n, min), max) : 0;
        last.current = clamped;
        onChange?.(clamped);
      }}
    />
  );
}

/* ------------------------------------------------------------------ *
 * Checkbox and switch
 * ------------------------------------------------------------------ */

export function Checkbox({ label, description, checked, onChange, disabled, id, ...rest }) {
  const generated = useId();
  const controlId = id || generated;
  return (
    <div className="checkbox">
      <input
        type="checkbox"
        id={controlId}
        checked={!!checked}
        onChange={(e) => onChange?.(e.target.checked)}
        disabled={disabled}
        {...rest}
      />
      <label htmlFor={controlId}>
        <span className="checkbox-label-text">{label}</span>
        {description && <div className="checkbox-description">{description}</div>}
      </label>
    </div>
  );
}

export function Switch({ label, description, checked, onChange, disabled, id }) {
  const generated = useId();
  const controlId = id || generated;
  return (
    <div className="row-between gap-4">
      <label htmlFor={controlId} style={{ cursor: 'pointer' }}>
        <span className="checkbox-label-text">{label}</span>
        {description && <div className="checkbox-description">{description}</div>}
      </label>
      <span className="switch">
        <input
          type="checkbox"
          role="switch"
          id={controlId}
          checked={!!checked}
          onChange={(e) => onChange?.(e.target.checked)}
          disabled={disabled}
        />
        <span className="switch-track" aria-hidden="true">
          <span className="switch-thumb" />
        </span>
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Combobox — searchable select
 * ------------------------------------------------------------------ */

/**
 * Fully keyboard-navigable. v1's equivalent rendered options as
 * `<div onClick>` with no role, no focus and no keyboard handler, and called
 * `item.label.toLowerCase()` unguarded so a single option with a missing label
 * threw and broke the whole picker.
 */
export function Combobox({
  label,
  hint,
  error,
  required,
  value,
  onChange,
  options = [],
  placeholder = 'Search…',
  emptyMessage = 'No matches',
  renderOption,
  onCreate,
  createLabel = 'Create',
  disabled,
  id,
  span2,
  loading = false,
}) {
  const { controlId, aria } = useFieldAria({ id, error, hint });
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [position, setPosition] = useState(null);
  const wrapRef = useRef(null);
  /** The input group, which is what the portalled list is measured against. */
  const fieldRef = useRef(null);
  const listRef = useRef(null);
  const listId = `${controlId}-listbox`;

  const selected = useMemo(
    () => options.find((o) => o.value === value) || null,
    [options, value]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      // Guard every field: a missing label must not throw.
      [o.label, o.meta, o.keywords]
        .filter(Boolean)
        .some((f) => String(f).toLowerCase().includes(q))
    );
  }, [options, search]);

  /* --- positioning --------------------------------------------------
   *
   * THE LIST IS PORTALLED TO <body> AND POSITIONED AGAINST THE VIEWPORT,
   * exactly as `Select` above does, and for exactly the same reason.
   *
   * It used to be an absolutely-positioned child of the field. That works
   * until the field sits inside something that clips: the voucher line grid
   * is a table inside a card, and `.table-wrap` scrolls. An absolutely
   * positioned popup cannot escape an ancestor's overflow, so on the Contra,
   * Payment, Receipt and Journal forms the dropdown was cut off at the bottom
   * edge of the grid — which hid the "Add New Ledger" row that had just been
   * added to the end of it, and clipped the option list itself whenever the
   * row sat near the bottom of the table.
   *
   * Nothing parented to <body> can be clipped by a scroller, a card with
   * `overflow: hidden`, or a modal.
   * ------------------------------------------------------------------ */

  const reposition = useCallback(() => {
    const node = fieldRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom - SELECT_LIST_OFFSET - SELECT_VIEWPORT_PAD;
    const above = rect.top - SELECT_LIST_OFFSET - SELECT_VIEWPORT_PAD;
    /* Flip up only when below genuinely cannot hold the list and above is
       roomier — the last row of a long grid is exactly this case. */
    const flip = below < Math.min(SELECT_LIST_MAX_HEIGHT, above) && above > below;
    const space = Math.max(flip ? above : below, 96);
    const next = {
      left: rect.left,
      width: rect.width,
      maxHeight: Math.min(SELECT_LIST_MAX_HEIGHT, space),
      flip,
      top: flip ? undefined : rect.bottom + SELECT_LIST_OFFSET,
      bottom: flip ? window.innerHeight - rect.top + SELECT_LIST_OFFSET : undefined,
    };
    // Scroll fires continuously; re-render only when the numbers actually move.
    setPosition((prev) =>
      prev && Object.keys(next).every((k) => prev[k] === next[k]) ? prev : next
    );
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    reposition();
    // `true` catches scrolls in any ancestor scroller, not just the window.
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  /* Click outside. The list is portalled, so "inside" means the field OR the
     list — testing the wrapper alone would treat every click on an option as
     an outside click and close the popup before it could be chosen. */
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (listRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('touchstart', onDocClick);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
    };
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [search, open]);

  /* Keep the active row in view while arrowing through a long ledger list —
     including the create row pinned at the end of it. */
  useEffect(() => {
    if (!open) return;
    const node = listRef.current?.querySelector(`[data-index="${activeIndex}"]`);
    // Optional-called: jsdom and older WebViews do not implement it.
    node?.scrollIntoView?.({ block: 'nearest' });
  }, [open, activeIndex, position]);

  const commit = (option) => {
    onChange?.(option ? option.value : null, option);
    setOpen(false);
    setSearch('');
  };

  /**
   * The create row is ALWAYS in the list when `onCreate` is given.
   *
   * It used to appear only when the search matched nothing, which meant the
   * only way to discover "add a new one" was to type a name that did not
   * exist — so on a Receipt or Payment Voucher, adding a ledger looked
   * impossible. It is now the last row, reachable by mouse and by keyboard,
   * and it carries the typed text when there is any so the new record opens
   * pre-named.
   */
  const term = search.trim();
  const canCreate = typeof onCreate === 'function';
  const createIndex = canCreate ? filtered.length : -1;
  const navCount = filtered.length + (canCreate ? 1 : 0);

  const runCreate = () => {
    onCreate?.(term);
    setOpen(false);
    setSearch('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      else setActiveIndex((i) => Math.min(i + 1, navCount - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (!open) return;
      if (canCreate && activeIndex === createIndex) {
        e.preventDefault();
        runCreate();
      } else if (filtered[activeIndex]) {
        e.preventDefault();
        commit(filtered[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  /**
   * The popup, portalled to <body> so nothing can clip it.
   *
   * `position` is measured from the field. Until it exists the list is painted
   * transparent and inert rather than at the window's top-left corner, which
   * is where an unpositioned fixed element would flash for one frame.
   */
  const list = (
    <ul
      ref={listRef}
      className={cx('combobox-list', position?.flip && 'combobox-list-flipped')}
      id={listId}
      role="listbox"
      aria-label={label}
      style={
        position
          ? {
              left: position.left,
              width: position.width,
              maxHeight: position.maxHeight,
              ...(position.flip ? { bottom: position.bottom } : { top: position.top }),
            }
          : { opacity: 0, pointerEvents: 'none' }
      }
    >
      {filtered.length === 0 && (
        <li className="combobox-empty" role="none">
          {emptyMessage}
        </li>
      )}
      {filtered.map((option, index) => (
        <li key={option.value} role="none">
          <button
            type="button"
            id={`${listId}-${index}`}
            role="option"
            aria-selected={option.value === value}
            data-index={index}
            data-active={index === activeIndex}
            className="combobox-option"
            onMouseEnter={() => setActiveIndex(index)}
            onClick={() => commit(option)}
          >
            {renderOption ? (
              renderOption(option)
            ) : (
              <span className="grow">
                <span className="combobox-option-title">{option.label}</span>
                {option.meta && <div className="combobox-option-meta">{option.meta}</div>}
              </span>
            )}
            {option.value === value && <CheckIcon size={14} aria-hidden="true" />}
          </button>
        </li>
      ))}
      {canCreate && (
        <li className="combobox-create-row" role="none">
          <button
            type="button"
            id={`${listId}-${createIndex}`}
            role="option"
            aria-selected={false}
            data-index={createIndex}
            data-active={activeIndex === createIndex}
            className="combobox-option combobox-create"
            onMouseEnter={() => setActiveIndex(createIndex)}
            onClick={runCreate}
          >
            <AddIcon size={14} aria-hidden="true" />
            <span className="grow">
              <span className="combobox-option-title">{createLabel}</span>
              {term && <div className="combobox-option-meta">“{term}”</div>}
            </span>
          </button>
        </li>
      )}
    </ul>
  );

  return (
    <Field label={label} hint={hint} error={error} required={required} htmlFor={controlId} span2={span2}>
      <div className="combobox" ref={wrapRef} data-open={open ? 'true' : 'false'}>
        <div className="input-group" ref={fieldRef}>
          <input
            className="input"
            type="text"
            role="combobox"
            autoComplete="off"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={open && filtered[activeIndex] ? `${listId}-${activeIndex}` : undefined}
            placeholder={selected ? selected.label : placeholder}
            value={open ? search : selected?.label || ''}
            disabled={disabled}
            onFocus={() => setOpen(true)}
            onChange={(e) => {
              setSearch(e.target.value);
              setOpen(true);
            }}
            onKeyDown={handleKeyDown}
            {...aria}
          />
          {selected && !disabled ? (
            <button
              type="button"
              className="input-affix"
              aria-label={`Clear ${label || 'selection'}`}
              onClick={() => commit(null)}
            >
              <CloseIcon size={14} />
            </button>
          ) : loading ? (
            <span className="input-affix" aria-hidden="true">
              <span className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />
            </span>
          ) : null}
        </div>

        {open && typeof document !== 'undefined' && createPortal(list, document.body)}
      </div>
    </Field>
  );
}

/* ------------------------------------------------------------------ *
 * Search input
 * ------------------------------------------------------------------ */

export function SearchInput({ value, onChange, placeholder = 'Search…', ariaLabel = 'Search', className }) {
  return (
    <div className={cx('input-group', className)}>
      <span className="input-affix" aria-hidden="true">
        <SearchIcon size={14} />
      </span>
      <input
        className="input"
        type="search"
        role="searchbox"
        aria-label={ariaLabel}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Date input
 * ------------------------------------------------------------------ */

export function DateInput({ label, hint, error, required, value, onChange, id, span2, ...rest }) {
  const { controlId, aria } = useFieldAria({ id, error, hint });
  return (
    <Field label={label} hint={hint} error={error} required={required} htmlFor={controlId} span2={span2}>
      <input
        type="date"
        className="input"
        value={value || ''}
        onChange={(e) => onChange?.(e.target.value)}
        required={required}
        {...aria}
        {...rest}
      />
    </Field>
  );
}
